// Založit klientku z kontaktu (mockup „Klientky bez Notionu“, varianta A, schváleno 1. 10. 2026).
// Po vypnutí Notionu klientka vznikne sama, jakmile zaplatí fakturu za spolupráci. Když ji
// trenér potřebuje v systému dřív, založí ji tohle tlačítko. Vidí ho owner, manažer a CSM,
// sales ne. Před založením okno s předvyplněnými údaji a výběrem balíčku.
import React, { useEffect, useState } from "react";
import { UserPlus, ChevronLeft, CircleCheck } from "lucide-react";
import { Modal, HlavickaOkna, Field } from "../ui.jsx";
import { toast } from "../Toast.jsx";
import { getCurrentUser } from "../../lib/auth.js";
import { apiFetch } from "../../lib/api.js";
import { salesPost, dm, denKratce, dnesIso } from "./salesApi.js";
import { Oznameni, RadekShrnuti } from "./salesUi.jsx";

const ROLE = new Set(["owner", "manager", "csm"]);
let _rezim = null;
export function nactiRezim() {
  if (!_rezim) _rezim = apiFetch("/api/sales?akce=notion_rezim").then((r) => (r.ok ? r.json() : {})).catch(() => ({}));
  return _rezim;
}
// Zkušební, který už proběhl (nezrušený). Bez něj se tlačítko neukazuje.
function probehlyZkusebni(k) {
  return (k.zkusebni || []).filter((z) => z.stav !== "zruseno" && z.probehl).sort((a, b) => String(b.datum).localeCompare(String(a.datum)))[0];
}
// 7. 10. 2026 (#139): stejná podmínka pro blok Výsledek, ať ví, jestli Deal jde zapsat.
export function dealMozny(k, rezim) {
  const role = String(getCurrentUser()?.role || "").toLowerCase();
  return ROLE.has(role) && !!rezim?.vypnuto && !!probehlyZkusebni(k) && k.klientka?.stav !== "Aktivní" && ["zkusebni", "neuzavreno"].includes(k.stav);
}

// kompaktni (7. 10. 2026): jen tlačítko do mřížky bloku Výsledek, bez oznámení.
export default function ZalozitKlientku({ k, onHotovo, kompaktni = false }) {
  const role = String(getCurrentUser()?.role || "").toLowerCase();
  const [rezim, setRezim] = useState(null);
  const [otevreno, setOtevreno] = useState(false);
  const [balicek, setBalicek] = useState("");
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  useEffect(() => { let zr = false; nactiRezim().then((r) => !zr && setRezim(r)); return () => { zr = true; }; }, []);

  const zk = probehlyZkusebni(k);
  const uzKlientka = k.klientka?.stav === "Aktivní";
  if (!ROLE.has(role) || !rezim?.vypnuto || !zk || uzKlientka || !["zkusebni", "neuzavreno"].includes(k.stav)) return null;

  const zalozit = async () => {
    if (!balicek) { setChyba("Vyber balíček."); return; }
    setBusy(true); setChyba("");
    try {
      const r = await salesPost("zalozit_klientku", { id: k.id, balicek });
      toast(r.akce === "zalozena" ? `${r.klient_name} je založená, najdeš ji v Klientech.` : `${r.klient_name} už v Klientech je, vrátil jsem jí stav Aktivní.`, "success");
      setOtevreno(false);
      onHotovo?.(r);
    } catch (e) { setChyba(e.message); } finally { setBusy(false); }
  };

  const okno = otevreno && (
        <Modal open onClose={() => setOtevreno(false)} size="md" plne plnaHlavicka
          header={<HlavickaOkna ikona={UserPlus} nazev="Založit klientku" popis={k.jmeno} onClose={() => setOtevreno(false)} />}
          footer={<>
            <button type="button" className="btn-secondary" onClick={() => setOtevreno(false)}><ChevronLeft className="h-4 w-4" />Zpět</button>
            <button type="button" className="btn-primary" onClick={zalozit} disabled={busy} aria-busy={busy}><CircleCheck className="h-4 w-4" />{busy ? "Zakládám…" : "Založit"}</button>
          </>}>
          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-1">
              <RadekShrnuti popisek="Jméno">{k.jmeno}</RadekShrnuti>
              <RadekShrnuti popisek="Kouč">{zk.trener || "doplň v kartě klientky"}</RadekShrnuti>
              <RadekShrnuti popisek="Lokalita">{zk.lokalita_nazev || zk.lokalita || "doplň v kartě klientky"}</RadekShrnuti>
              <RadekShrnuti popisek="Začátek">dnes, {dm(dnesIso())}</RadekShrnuti>
            </div>
            <Field label="Balíček">
              <select className="field" value={balicek} onChange={(e) => { setBalicek(e.target.value); setChyba(""); }}>
                <option value="">Vyber balíček</option>
                {(rezim.balicky || []).map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </Field>
            <Oznameni ton="znacka">Cíl, zdraví a dotazník se přenesou z kontaktu. Pak ji najdeš v Klientech a trenér jí může zapsat tréninky.</Oznameni>
            {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
          </div>
        </Modal>
  );
  if (kompaktni) {
    return (<>
      <button type="button" className="flex min-h-[44px] items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-brand-900 px-2 text-[13px] font-bold text-white transition-colors hover:bg-brand-950" title="Založí klientku v Klientech" onClick={() => { setChyba(""); setOtevreno(true); }}><UserPlus className="h-4 w-4" />Deal</button>
      {okno}
    </>);
  }
  return (
    <div className="space-y-2">
      <Oznameni ton="ok"><b>Zkušební proběhl {denKratce(zk.datum)}</b>{zk.trener ? ` u trenéra ${zk.trener}` : ""}. Klientka se založí sama, až zaplatí fakturu za spolupráci. Když ji trenér potřebuje dřív, založ ji tady.</Oznameni>
      <button type="button" className="btn w-full bg-emerald-600 !min-h-[52px] text-white hover:bg-emerald-700" onClick={() => { setChyba(""); setOtevreno(true); }}>
        <UserPlus className="h-5 w-5" />Založit klientku
      </button>
      {okno}
    </div>
  );
}
